import { useState, useEffect, useId } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Search, X } from 'lucide-react';
import { cn } from '@/lib/utils';

interface SearchBarProps {
  className?: string;
  placeholder?: string;
}

/**
 * SearchBar - Accessible search input for filtering experiences
 * 
 * WCAG 2.1 AA Features:
 * - Proper labeling with aria-label
 * - Visible focus indicators
 * - Clear button with accessible name
 * - Live region for announcing results
 * - Keyboard accessible
 */
export function SearchBar({ 
  className, 
  placeholder = 'Search experiences...' 
}: SearchBarProps) {
  const id = useId();
  const [searchParams, setSearchParams] = useSearchParams();
  const [value, setValue] = useState(searchParams.get('search') || '');
  const [isAnnouncing, setIsAnnouncing] = useState(false);

  // Debounce search
  useEffect(() => {
    const timer = setTimeout(() => {
      const params = new URLSearchParams(searchParams);
      if (value) {
        params.set('search', value);
      } else {
        params.delete('search');
      }
      setSearchParams(params);
      
      // Trigger screen reader announcement
      setIsAnnouncing(true);
      setTimeout(() => setIsAnnouncing(false), 100);
    }, 300);

    return () => clearTimeout(timer);
  }, [value, searchParams, setSearchParams]);

  const handleClear = () => {
    setValue('');
    const params = new URLSearchParams(searchParams);
    params.delete('search');
    setSearchParams(params);
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    // Allow Escape to clear the search
    if (e.key === 'Escape' && value) {
      e.preventDefault();
      handleClear();
    }
  };

  return (
    <div className={cn('relative', className)}>
      <label htmlFor={id} className="sr-only">
        Search experiences
      </label>
      
      <Search 
        className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground pointer-events-none" 
        aria-hidden="true"
      />
      
      <input
        id={id}
        type="search"
        value={value}
        onChange={(e) => setValue(e.target.value)}
        onKeyDown={handleKeyDown}
        placeholder={placeholder}
        autoComplete="off"
        autoCorrect="off"
        autoCapitalize="off"
        spellCheck="false"
        className={cn(
          'w-full rounded-lg border bg-background py-2.5 pl-10 pr-10 text-sm',
          'placeholder:text-muted-foreground',
          'focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2',
          'motion-safe:transition-shadow'
        )}
        aria-describedby={`${id}-hint`}
      />
      
      {/* Hidden hint for screen readers */}
      <span id={`${id}-hint`} className="sr-only">
        Type to search experiences. Press Escape to clear.
      </span>
      
      {/* Clear button */}
      {value && (
        <button
          type="button"
          onClick={handleClear}
          className={cn(
            'absolute right-3 top-1/2 -translate-y-1/2',
            'p-0.5 rounded-sm text-muted-foreground',
            'hover:text-foreground focus:text-foreground',
            'focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2',
            'motion-safe:transition-colors'
          )}
          aria-label="Clear search"
        >
          <X className="h-4 w-4" aria-hidden="true" />
        </button>
      )}
      
      {/* Live region for announcing search state */}
      <div 
        aria-live="polite" 
        aria-atomic="true" 
        className="sr-only"
      >
        {isAnnouncing && value && `Searching for ${value}`}
        {isAnnouncing && !value && 'Search cleared'}
      </div>
    </div>
  );
}
